import React, { useState, useCallback, useEffect } from 'react';
import ReactFlow, { 
  MiniMap, 
  Controls, 
  Background, 
  ReactFlowProvider,
  applyNodeChanges,
  applyEdgeChanges,
  Handle,
  Position
} from 'reactflow';
import 'reactflow/dist/style.css';
import { themeStyles, customStyles } from './ArtemisStyles';

// Custom node component for the simple journey
const SimpleJourneyNode = ({ data }) => {
  return (
    <div style={{
      padding: '20px 30px',
      borderRadius: '15px',
      background: data.background || 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
      border: `2px solid ${data.borderColor || '#DEE2E6'}`,
      boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
      minWidth: '200px',
      textAlign: 'center',
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.12)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)';
    }}>
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <div style={{ fontSize: '28px', marginBottom: '10px' }}>{data.icon}</div>
      <div style={{ 
        fontSize: '16px', 
        fontWeight: '600', 
        color: '#212529',
        marginBottom: '8px',
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif'
      }}>
        {data.title}
      </div>
      <div style={{ 
        fontSize: '13px', 
        color: '#6C757D',
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
        lineHeight: '1.5'
      }}>
        {data.description}
      </div>
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
    </div>
  );
};

const nodeTypes = {
  simpleJourney: SimpleJourneyNode
};

// Simple linear workflow nodes
const getInitialNodes = () => {
  const savedPositions = localStorage.getItem('artemisSimpleJourneyPositions');
  if (savedPositions) {
    return JSON.parse(savedPositions);
  }
  
  return [
    {
      id: '1',
      type: 'simpleJourney',
      data: { 
        icon: '📤',
        title: 'Upload Code',
        description: 'Drop your code or connect Git repository',
        background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
        borderColor: '#2196F3'
      },
      position: { x: 50, y: 250 },
    },
    {
      id: '2',
      type: 'simpleJourney',
      data: { 
        icon: '🔍',
        title: 'AI Analysis',
        description: 'Artemis identifies optimization opportunities',
        background: 'linear-gradient(135deg, #F3E5F5 0%, #E1BEE7 100%)',
        borderColor: '#9C27B0'
      },
      position: { x: 350, y: 250 },
    },
    {
      id: '3',
      type: 'simpleJourney',
      data: { 
        icon: '🧬',
        title: 'Evolution',
        description: 'AI generates and evolves solutions',
        background: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)',
        borderColor: '#F4511E'
      },
      position: { x: 650, y: 250 },
    },
    {
      id: '4',
      type: 'simpleJourney',
      data: { 
        icon: '✅',
        title: 'Validation',
        description: 'Tests & benchmarks ensure quality',
        background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
        borderColor: '#4CAF50'
      },
      position: { x: 950, y: 250 },
    },
    {
      id: '5',
      type: 'simpleJourney',
      data: { 
        icon: '🚀',
        title: 'Deploy',
        description: 'Get optimized code via PR or download',
        background: 'linear-gradient(135deg, #FCE4EC 0%, #F8BBD0 100%)',
        borderColor: '#E91E63'
      },
      position: { x: 1250, y: 250 },
    }
  ];
};

const simpleEdges = [
  { 
    id: 'e1-2', 
    source: '1', 
    target: '2', 
    animated: true, 
    style: { 
      stroke: '#2196F3', 
      strokeWidth: 3,
      strokeDasharray: '0'
    },
    type: 'smoothstep'
  },
  { 
    id: 'e2-3', 
    source: '2', 
    target: '3', 
    animated: true, 
    style: { 
      stroke: '#9C27B0', 
      strokeWidth: 3,
      strokeDasharray: '0'
    },
    type: 'smoothstep'
  },
  { 
    id: 'e3-4', 
    source: '3', 
    target: '4', 
    animated: true, 
    style: { 
      stroke: '#F4511E', 
      strokeWidth: 3,
      strokeDasharray: '0'
    },
    type: 'smoothstep'
  },
  { 
    id: 'e4-5', 
    source: '4', 
    target: '5', 
    animated: true, 
    style: { 
      stroke: '#4CAF50', 
      strokeWidth: 3,
      strokeDasharray: '0'
    },
    type: 'smoothstep'
  }
];

const ArtemisSimpleJourney = () => {
  const [nodes, setNodes] = useState(getInitialNodes());
  const [edges, setEdges] = useState(simpleEdges);

  // Add custom styles on mount
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = customStyles;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Save positions whenever nodes change
  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => {
      const updatedNodes = applyNodeChanges(changes, nds);
      // Save positions to localStorage
      localStorage.setItem('artemisSimpleJourneyPositions', JSON.stringify(updatedNodes));
      return updatedNodes;
    });
  }, []);

  const onEdgesChange = useCallback((changes) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
  }, []);

  // Reset positions
  const resetPositions = () => {
    localStorage.removeItem('artemisSimpleJourneyPositions');
    setNodes(getInitialNodes());
  };

  return (
    <ReactFlowProvider>
      <div style={{ 
        height: '100vh', 
        width: '100%', 
        background: 'linear-gradient(135deg, #FAFAFA 0%, #F5F5F5 100%)',
        position: 'relative'
      }}>
        <h2 style={{ 
          textAlign: 'center', 
          color: '#212529', 
          padding: '20px', 
          margin: '0',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          fontSize: '28px',
          fontWeight: '700'
        }}>
          ✨ Artemis Simple Journey
        </h2>
        
        <div style={{
          position: 'absolute',
          top: '70px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '10px 25px',
          background: 'linear-gradient(135deg, #F4511E 0%, #FF6D00 100%)',
          borderRadius: '25px',
          color: 'white',
          fontSize: '15px',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          fontWeight: '500',
          zIndex: 100,
          boxShadow: '0 4px 15px rgba(244, 81, 30, 0.3)'
        }}>
          From code to optimization in 5 simple steps
        </div>

        {/* Reset button */}
        <button
          onClick={resetPositions}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            padding: '8px 16px',
            background: '#6C757D',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '13px',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
            cursor: 'pointer',
            zIndex: 100,
            transition: 'background 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.background = '#5C636A'}
          onMouseLeave={(e) => e.target.style.background = '#6C757D'}
        >
          Reset Layout
        </button>
        
        <div style={{ 
          position: 'absolute',
          top: '120px',
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: 'calc(100% - 120px)'
        }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{
              padding: 0.3,
              minZoom: 0.5,
              maxZoom: 1.2
            }}
            minZoom={0.3}
            maxZoom={1.5}
            defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
            nodesDraggable={true}
            nodesConnectable={false}
            elementsSelectable={true}
            panOnDrag={true}
            panOnScroll={true}
            zoomOnScroll={true}
            zoomOnPinch={true}
          >
            <MiniMap 
              style={{
                height: 100,
                width: 150,
                backgroundColor: 'rgba(255,255,255,0.9)',
                border: '1px solid #E0E0E0'
              }}
              nodeColor={(node) => {
                return node.data?.borderColor || '#6C757D';
              }}
              nodeStrokeColor={() => '#495057'}
              nodeBorderRadius={8}
            />
            <Controls />
            <Background 
              variant="dots" 
              color="#E0E0E0" 
              gap={25} 
              size={1} 
            />
          </ReactFlow>
        </div>

        {/* Bottom Info Panel */}
        <div style={{
          position: 'absolute',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(255,255,255,0.95)',
          padding: '20px 40px',
          borderRadius: '20px',
          border: '1px solid #E0E0E0',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          maxWidth: '800px'
        }}>
          <div style={{
            display: 'flex',
            gap: '40px',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#F4511E', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}>
                10x
              </div>
              <div style={{ fontSize: '12px', color: '#6C757D', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}>
                Faster Code
              </div>
            </div>
            <div style={{ width: '1px', height: '40px', background: '#E0E0E0' }} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#4CAF50', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}>
                100%
              </div>
              <div style={{ fontSize: '12px', color: '#6C757D', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}>
                Validated
              </div>
            </div>
            <div style={{ width: '1px', height: '40px', background: '#E0E0E0' }} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#2196F3', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}>
                5min
              </div>
              <div style={{ fontSize: '12px', color: '#6C757D', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}>
                To Results
              </div>
            </div>
          </div>
        </div>
      </div>
    </ReactFlowProvider>
  );
};

export default ArtemisSimpleJourney; 